import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, Input, Output, ContentChild, ViewChildren, NgModule } from '@angular/core';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';

class KeyboardNavigationService {
    constructor() { }
    // Calculates the correct focus using the DOM, returns the
    // index of the next node to focus, and dispatches events if necessary.
    getKeyAction(event, items, index) {
        if (event.key === 'Tab' || event.keyCode === 9 || event.which === 9) {
            return this.tab(event, index, items);
        }
        if (this.isInteractiveElementFocused()) {
            return null;
        }
        const handler = this.getKeyHandler(event);
        if (handler !== null) {
            event.preventDefault();
            return handler.bind(this)(index, items);
        }
        return null;
    }
    isInteractiveElementFocused() {
        const tagName = document && document.activeElement && document.activeElement.tagName
            ? document.activeElement.tagName.toLowerCase()
            : '';
        return ['a', 'button', 'input', 'option', 'select', 'textarea'].indexOf(tagName) !== -1;
    }
    /**
     * Cross-browser compatible keypress matching, see link below for details.
     * Returns handler for actionable keyboard navigation keys, else null.
     * https://stackoverflow.com/questions/15108670/cross-browser-cross-os-way-to-get-keycode-character-pressed-onkeydown-event
     */
    getKeyHandler(event) {
        if (event.key === 'ArrowRight' ||
            event.key === 'Right' || // IE/Edge specific value
            event.keyCode === 39 ||
            event.which === 39) {
            return this.arrowRight;
        }
        if (event.key === 'ArrowLeft' ||
            event.key === 'Left' || // IE/Edge specific value
            event.keyCode === 37 ||
            event.which === 37) {
            return this.arrowLeft;
        }
        if (event.key === 'ArrowUp' ||
            event.key === 'Up' || // IE/Edge specific value
            event.keyCode === 38 ||
            event.which === 38) {
            return this.arrowUp;
        }
        if (event.key === 'ArrowDown' ||
            event.key === 'Down' || // IE/Edge specific value
            event.keyCode === 40 ||
            event.which === 40) {
            return this.arrowDown;
        }
        if (event.key === 'Home' || event.keyCode === 36 || event.which === 36) {
            return this.home;
        }
        if (event.key === 'End' || event.keyCode === 35 || event.which === 35) {
            return this.end;
        }
        // enter and space bar both toggle expansion state
        if (event.key === 'Enter' ||
            event.key === ' ' ||
            event.keyCode === 13 ||
            event.which === 13 ||
            event.keyCode === 32 ||
            event.which === 32) {
            return this.spaceBar;
        }
        return null;
    }
    arrowUp(index, items) {
        const newIndex = index - 1 >= 0 ? index - 1 : index;
        return {
            newIndex,
            emitToggle: false,
        };
    }
    arrowDown(index, items) {
        const newIndex = index + 1 < items.length ? index + 1 : index;
        return {
            newIndex,
            emitToggle: false,
        };
    }
    // If node is collapsed, expanded it. If not expanded, go to the next node in the list.
    arrowRight(index, items) {
        const currentNode = items[index];
        const ariaExpanded = currentNode.getAttribute('aria-expanded');
        if (ariaExpanded === 'false') {
            return {
                newIndex: index,
                emitToggle: true,
            };
        }
        // node is not a folder, do nothing.
        return this.arrowDown(index, items);
    }
    // If the node is expanded, collapse it. If not, move to its parent.
    arrowLeft(index, items) {
        const currentNode = items[index];
        const ariaExpanded = currentNode.getAttribute('aria-expanded');
        if (ariaExpanded === 'true') {
            return {
                newIndex: index,
                emitToggle: true,
            };
        }
        const parentIndex = this.getParentIndex(currentNode, items);
        return {
            newIndex: parentIndex === -1 ? index : parentIndex,
            emitToggle: false,
        };
    }
    // If the node is a folder, toggle between collapsed and expanded.  If not, do nothing.
    spaceBar(index, items) {
        const currentNode = items[index];
        const ariaExpanded = currentNode.getAttribute('aria-expanded');
        // node is a folder, already expanded. Collapse it.
        if (ariaExpanded === 'true') {
            return {
                newIndex: index,
                emitToggle: true,
            };
        }
        // node is a collapsed folder, expand it.
        if (ariaExpanded === 'false') {
            return {
                newIndex: index,
                emitToggle: true,
            };
        }
        // node is not a folder, do nothing.
        return {
            newIndex: index,
            emitToggle: false,
        };
    }
    tab(event, index, items) {
        const currentNode = items[index];
        const activeElement = document.activeElement;
        const focusableElements = [
            'a[href]',
            'area[href]',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            '[tabindex="0"]:not(.modal-content)',
        ].join(', ');
        const focusableChildren = currentNode.querySelectorAll(focusableElements);
        const isFirstChildFocused = focusableChildren.length > 0 && activeElement === focusableChildren[0];
        const isLastChildFocused = focusableChildren.length > 0 && activeElement === focusableChildren[focusableChildren.length - 1];
        const isNodeWrapperFocused = activeElement === currentNode;
        if (focusableChildren.length === 0) {
            return null;
        }
        if (!event.shiftKey && isLastChildFocused && index < items.length - 1) {
            // return focus to tree, on next node wrapper.
            event.preventDefault();
            return {
                newIndex: index + 1,
                emitToggle: false,
            };
        }
        if (event.shiftKey && isFirstChildFocused && index > 0) {
            // return focus to the current node wrapper.
            event.preventDefault();
            currentNode.focus();
            return null;
        }
        if (event.shiftKey && isNodeWrapperFocused && index > 0) {
            // TODO: Investigate way to handle this. Leave the tree? go to previous node?
            event.preventDefault();
            return {
                newIndex: index - 1,
                emitToggle: false,
            };
        }
        return null;
    }
    // jump to first node in the list
    home(index, items) {
        return {
            newIndex: 0,
            emitToggle: false,
        };
    }
    // jump to last node in the list
    end(index, items) {
        const newIndex = items.length > 0 ? items.length - 1 : index;
        return {
            newIndex,
            emitToggle: false,
        };
    }
    getParentIndex(node, items) {
        let parentIndex = -1;
        let parentNode;
        const parentElement = node.parentElement || null;
        parentNode = parentElement ? parentElement.closest('ul') : null;
        try {
            // get first node-wrapper inside parent li of the child list.
            parentNode = parentNode.parentElement.firstElementChild;
            // IE-friendly parent index lookup
            Array.prototype.forEach.call(items, (item, index) => {
                if (item.id === parentNode.id) {
                    parentIndex = index;
                }
            });
        }
        catch (err) {
            return -1;
        }
        return parentIndex;
    }
    getIndexById(id, items) {
        let result = -1;
        // IE-friendly parent index lookup
        Array.prototype.forEach.call(items, (item, index) => {
            if (item.id === 'tree-node-' + id) {
                result = index;
            }
        });
        return result;
    }
    getNodeId(node) {
        return node.id.replace('tree-node-', '');
    }
}
KeyboardNavigationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: KeyboardNavigationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
KeyboardNavigationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: KeyboardNavigationService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: KeyboardNavigationService, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

class BonsaiTreeComponent {
    constructor(elementRef, keyboardNavService) {
        this.elementRef = elementRef;
        this.keyboardNavService = keyboardNavService;
        this.expandedNodes = {};
        this.focusedNodeIndex = 0;
        this.hideOutlines = true;
        this.nodeClick = new EventEmitter();
        this.expanderClick = new EventEmitter();
    }
    ngOnInit() {
        if (!this.rootNodeId || !this.nodesById[this.rootNodeId]) {
            console.warn('Ngx-bonsai root node is undefined! Tree will not render.');
        }
        this.focusedNodeId = this.initialNodeList.length > 0 ? this.initialNodeList[0] : '';
    }
    ngOnChanges(changes) {
        setTimeout(() => {
            // Fix the focus when new data comes in.
            const visibleNodes = this.elementRef.nativeElement.querySelectorAll('.node-wrapper[tabindex]');
            this.checkFocusedIndex(visibleNodes);
        });
    }
    checkFocusedIndex(visibleNodes) {
        const activeElement = document ? document.activeElement : null;
        const currentFocus = activeElement
            ? activeElement.closest('.node-wrapper[tabindex]')
            : null;
        if (currentFocus) {
            const focusedId = this.keyboardNavService.getNodeId(currentFocus);
            const index = this.keyboardNavService.getIndexById(focusedId, visibleNodes);
            if (index !== -1) {
                this.focusedNodeIndex = index;
                this.focusedNodeId = focusedId;
            }
        }
        else if (visibleNodes.length) {
            // Data changed while the focus was not within the tree, just focus the first element.
            const focusedId = this.keyboardNavService.getNodeId(visibleNodes[0]);
            const index = this.keyboardNavService.getIndexById(focusedId, visibleNodes);
            if (index !== -1) {
                this.focusedNodeIndex = index;
                this.focusedNodeId = focusedId;
            }
        }
    }
    expandNode(nodeId) {
        this.expanderClick.emit(nodeId);
        this.hideOutlines = true;
        setTimeout(() => {
            // Fix the focus if the current focus was collapsed or removed.
            // ngOnChanges isn't fired if a property on an object was changed.
            // https://stackoverflow.com/questions/34796901/angular2-change-detection-ngonchanges-not-firing-for-nested-object
            // Assume the changes will occur before the event loop triggers this.
            const visibleNodes = this.elementRef.nativeElement.querySelectorAll('.node-wrapper[tabindex]');
            this.checkFocusedIndex(visibleNodes);
        });
    }
    get initialNodeList() {
        if (this.nodesById && this.nodesById[this.rootNodeId]) {
            const rootnode = this.nodesById[this.rootNodeId];
            return rootnode.childIds || [];
        }
        return [];
    }
    isEmpty(node) {
        if (!this.showExpanderToggle(node)) {
            return false;
        }
        const hasChildIds = node.childIds ? node.childIds.length === 0 : false;
        return !this.isNodeLoading(node) && hasChildIds;
    }
    isAriaBusy(node) {
        // only apply aria-busy to nodes that have been expanded at least once, and are not yet loaded.
        if (this.isLoading && node.id in this.expandedNodes) {
            return this.isLoading(node);
        }
        return null;
    }
    isAriaExpanded(node) {
        if (!this.showExpanderToggle(node)) {
            return null;
        }
        return !!this.expandedNodes[node.id];
    }
    isNodeLoading(node) {
        if (!this.isLoading) {
            return false;
        }
        return this.isLoading(node);
    }
    showExpanderToggle(node) {
        if (this.showToggleFn) {
            return this.showToggleFn(node);
        }
        return !!node.childIds && node.childIds.length > 0;
    }
    trackById(index, nodeId) {
        return nodeId;
    }
    /**
     * Sets the active node.
     * Scrolls it to the top of a container if given.
     *
     * Useful for when Bonsai is within a modal or scrollable div, such as a drop-down for navigation.
     */
    focusNode(nodeId, container) {
        const visibleNodes = this.elementRef.nativeElement.querySelectorAll('.node-wrapper[tabindex]');
        const index = this.keyboardNavService.getIndexById(nodeId, visibleNodes);
        if (index !== -1) {
            this.focusedNodeIndex = index;
            this.focusedNodeId = nodeId;
            visibleNodes[index].focus();
            if (container) {
                container.nativeElement.scrollTop = visibleNodes[index].offsetTop - 10;
            }
        }
    }
    handleKeydown(event) {
        const visibleNodes = this.elementRef.nativeElement.querySelectorAll('.node-wrapper[tabindex]');
        // helps maintain first-time focus events, and switching between keyboard/mouse navigation.
        this.checkFocusedIndex(visibleNodes);
        const action = this.keyboardNavService.getKeyAction(event, visibleNodes, this.focusedNodeIndex);
        if (action === null) {
            return;
        }
        this.hideOutlines = false;
        if (action.newIndex !== this.focusedNodeIndex) {
            this.focusedNodeIndex = action.newIndex;
            this.focusedNodeId = this.keyboardNavService.getNodeId(visibleNodes[action.newIndex]);
            setTimeout(() => {
                visibleNodes[action.newIndex].focus();
            });
        }
        if (action.emitToggle) {
            this.expanderClick.emit(this.focusedNodeId);
        }
    }
}
BonsaiTreeComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: BonsaiTreeComponent, deps: [{ token: i0.ElementRef }, { token: KeyboardNavigationService }], target: i0.ɵɵFactoryTarget.Component });
BonsaiTreeComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.1.2", type: BonsaiTreeComponent, selector: "ngx-bonsai", inputs: { nodesById: "nodesById", rootNodeId: "rootNodeId", expandedNodes: "expandedNodes", showToggleFn: "showToggleFn", isLoading: "isLoading" }, outputs: { expanderClick: "expanderClick", nodeClick: "nodeClick" }, queries: [{ propertyName: "template", first: true, predicate: ["treeNodeTemplate"], descendants: true }, { propertyName: "loadingTemplate", first: true, predicate: ["loadingTemplate"], descendants: true }, { propertyName: "emptyTemplate", first: true, predicate: ["emptyTemplate"], descendants: true }], viewQueries: [{ propertyName: "listItems", predicate: ["listItem"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<ul\n  role=\"tree\"\n  (keydown)=\"handleKeydown($event)\"\n  [class.outline-none]=\"hideOutlines\"\n  (keyup.tab)=\"hideOutlines = false\"\n>\n  <ng-container\n    [ngTemplateOutlet]=\"flatBonsaiTemplate\"\n    [ngTemplateOutletContext]=\"{ $implicit: initialNodeList, depth: 1 }\"\n  >\n  </ng-container>\n</ul>\n\n<!-- Root Template -->\n<ng-template #flatBonsaiTemplate let-nodes let-depth=\"depth\">\n  <ng-container *ngFor=\"let nodeId of nodes; let i = index; trackBy: trackById\">\n    <li *ngIf=\"nodesById[nodeId]\" role=\"none\">\n      <!-- Use #nodeTemplate, and pass it nodesById[nodeId] as its context. -->\n      <ng-container\n        [ngTemplateOutlet]=\"nodeTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: nodesById[nodeId], depth: depth }\"\n      ></ng-container>\n    </li>\n  </ng-container>\n</ng-template>\n\n<!-- Template for individual nodes, allows us to reference the node directly. Let-node directive assigns its context to local node variable. -->\n<ng-template #nodeTemplate let-node let-depth=\"depth\">\n  <div\n    class=\"node-wrapper\"\n    role=\"treeitem\"\n    [attr.tabindex]=\"node.id === focusedNodeId ? 0 : -1\"\n    [attr.aria-expanded]=\"isAriaExpanded(node)\"\n    [attr.aria-busy]=\"isAriaBusy(node)\"\n    [attr.aria-level]=\"depth\"\n    [id]=\"'tree-node-' + node.id\"\n  >\n    <div\n      *ngIf=\"showExpanderToggle(node); else togglePlaceholder\"\n      class=\"toggle-children-wrapper\"\n      [class.expanded]=\"expandedNodes[node.id]\"\n      (click)=\"expandNode(node.id)\"\n    >\n      <div class=\"toggle-children dpg-icon-dropdown-arrow-right\"></div>\n    </div>\n    <div class=\"node-content-wrapper\">\n      <ng-container\n        [ngTemplateOutlet]=\"template || defaultTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: node, depth: depth }\"\n      >\n      </ng-container>\n    </div>\n  </div>\n  <ul *ngIf=\"expandedNodes[node.id]\" class=\"tree-children\" role=\"tree\">\n    <!-- Show loaded children, if any exist. -->\n    <ng-container\n      *ngTemplateOutlet=\"flatBonsaiTemplate; context: { $implicit: node.childIds || [], depth: depth + 1 }\"\n    ></ng-container>\n\n    <!-- Show spinner in list after any loaded children -->\n    <li *ngIf=\"isNodeLoading(node)\" role=\"treeitem\">\n      <div class=\"node-wrapper\">\n        <div class=\"node-content-wrapper loading\">\n          <ng-container [ngTemplateOutlet]=\"loadingTemplate || defaultLoadingTemplate\"></ng-container>\n        </div>\n      </div>\n    </li>\n\n    <!-- If not loading and no children exist, show placeholder with empty message. -->\n    <li *ngIf=\"isEmpty(node)\" role=\"treeitem\">\n      <div class=\"node-wrapper\">\n        <div class=\"node-content-wrapper empty\">\n          <ng-container [ngTemplateOutlet]=\"emptyTemplate || defaultEmptyTemplate\"></ng-container>\n        </div>\n      </div>\n    </li>\n  </ul>\n</ng-template>\n\n<ng-template #togglePlaceholder>\n  <span class=\"toggle-children-placeholder\"></span>\n</ng-template>\n\n<ng-template #defaultTemplate let-node>\n  <a class=\"ahe-ui-link node-title\" href=\"javascript:void(0)\" (click)=\"nodeClick.emit(node)\">\n    {{ node?.title }}\n  </a>\n</ng-template>\n\n<ng-template #defaultLoadingTemplate>\n  <div class=\"ahe-ui-loading-spinner\">\n    <div class=\"loader\">\n      <span class=\"sr-only\">Content is loading</span>\n      <svg class=\"circular\" viewBox=\"25 25 50 50\" focusable=\"false\" aria-hidden=\"true\">\n        <circle\n          class=\"path\"\n          cx=\"50\"\n          cy=\"50\"\n          r=\"20\"\n          fill=\"none\"\n          stroke-width=\"2\"\n          stroke-miterlimit=\"10\"\n        ></circle>\n      </svg>\n    </div>\n  </div>\n</ng-template>\n\n<ng-template #defaultEmptyTemplate> Empty </ng-template>\n", styles: [".dpg-icon-dropdown-arrow-right:before,.ahe-icon-dropdown-arrow-right:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23265867' d='M10.343 6.343L16 12l-5.657 5.657-1.414-1.414L13.172 12 8.929 7.757z'/%3E%3C/svg%3E\")}.btn-secondary-light .dpg-icon-dropdown-arrow-right:before,.btn-secondary-light .ahe-icon-dropdown-arrow-right:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M10.343 6.343L16 12l-5.657 5.657-1.414-1.414L13.172 12 8.929 7.757z'/%3E%3C/svg%3E\")}.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .dpg-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .ahe-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .dpg-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .ahe-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .dpg-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .ahe-icon-dropdown-arrow-right:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M10.343 6.343L16 12l-5.657 5.657-1.414-1.414L13.172 12 8.929 7.757z'/%3E%3C/svg%3E\")}ul{list-style:none;padding:0}ul.tree-children{padding-left:33px;overflow:visible}ul li{margin:3px 0}.outline-none{outline:none}.toggle-children-wrapper{border:1px solid #d7d7d7;margin:0 3px 0 0;padding:0;min-width:30px;min-height:48px;background:#f7f7f7;display:flex;justify-content:center;align-items:center;overflow:hidden;cursor:pointer}.toggle-children-wrapper .toggle-children{background:none;margin:0 auto;transform:rotate(0);position:relative;transition:transform .25s ease}.toggle-children-wrapper .toggle-children:before{width:28px;height:28px;cursor:pointer;display:block;text-align:center}.toggle-children-wrapper.expanded .toggle-children{transform:rotate(90deg)}.outline-none,.outline-none div{outline:none}.node-wrapper{display:flex;align-items:stretch;font-family:proxima_novaregular}.node-wrapper .toggle-children-placeholder{background-color:#f7f7f7;display:block;top:0;padding:0;margin:0;min-width:33px;height:0}.node-wrapper .node-content-wrapper{position:relative;display:flex;align-items:center;background-color:#fff;border:1px solid #d7d7d7;flex-grow:1;flex-basis:auto;min-height:48px;padding:8px}.node-wrapper .node-content-wrapper.loading{display:block;text-align:center;line-height:32px}.node-wrapper .node-content-wrapper.empty{display:block;text-align:center;border:1px dashed #d7d7d7;line-height:32px}.node-wrapper .node-content-wrapper .ahe-ui-loading-spinner{height:32px;width:32px;display:inline-block}.node-wrapper .node-content-wrapper a.node-title{font-size:16px;font-weight:500;border:transparent;text-decoration:none;color:#007c91;border-bottom:1px solid transparent}.node-wrapper .node-content-wrapper a.node-title:hover{border-bottom:1px solid #007c91}.node-wrapper .node-content-wrapper a.node-title:focus{border:1px solid #007c91}\n"], directives: [{ type: i2.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: BonsaiTreeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ngx-bonsai', template: "<ul\n  role=\"tree\"\n  (keydown)=\"handleKeydown($event)\"\n  [class.outline-none]=\"hideOutlines\"\n  (keyup.tab)=\"hideOutlines = false\"\n>\n  <ng-container\n    [ngTemplateOutlet]=\"flatBonsaiTemplate\"\n    [ngTemplateOutletContext]=\"{ $implicit: initialNodeList, depth: 1 }\"\n  >\n  </ng-container>\n</ul>\n\n<!-- Root Template -->\n<ng-template #flatBonsaiTemplate let-nodes let-depth=\"depth\">\n  <ng-container *ngFor=\"let nodeId of nodes; let i = index; trackBy: trackById\">\n    <li *ngIf=\"nodesById[nodeId]\" role=\"none\">\n      <!-- Use #nodeTemplate, and pass it nodesById[nodeId] as its context. -->\n      <ng-container\n        [ngTemplateOutlet]=\"nodeTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: nodesById[nodeId], depth: depth }\"\n      ></ng-container>\n    </li>\n  </ng-container>\n</ng-template>\n\n<!-- Template for individual nodes, allows us to reference the node directly. Let-node directive assigns its context to local node variable. -->\n<ng-template #nodeTemplate let-node let-depth=\"depth\">\n  <div\n    class=\"node-wrapper\"\n    role=\"treeitem\"\n    [attr.tabindex]=\"node.id === focusedNodeId ? 0 : -1\"\n    [attr.aria-expanded]=\"isAriaExpanded(node)\"\n    [attr.aria-busy]=\"isAriaBusy(node)\"\n    [attr.aria-level]=\"depth\"\n    [id]=\"'tree-node-' + node.id\"\n  >\n    <div\n      *ngIf=\"showExpanderToggle(node); else togglePlaceholder\"\n      class=\"toggle-children-wrapper\"\n      [class.expanded]=\"expandedNodes[node.id]\"\n      (click)=\"expandNode(node.id)\"\n    >\n      <div class=\"toggle-children dpg-icon-dropdown-arrow-right\"></div>\n    </div>\n    <div class=\"node-content-wrapper\">\n      <ng-container\n        [ngTemplateOutlet]=\"template || defaultTemplate\"\n        [ngTemplateOutletContext]=\"{ $implicit: node, depth: depth }\"\n      >\n      </ng-container>\n    </div>\n  </div>\n  <ul *ngIf=\"expandedNodes[node.id]\" class=\"tree-children\" role=\"tree\">\n    <!-- Show loaded children, if any exist. -->\n    <ng-container\n      *ngTemplateOutlet=\"flatBonsaiTemplate; context: { $implicit: node.childIds || [], depth: depth + 1 }\"\n    ></ng-container>\n\n    <!-- Show spinner in list after any loaded children -->\n    <li *ngIf=\"isNodeLoading(node)\" role=\"treeitem\">\n      <div class=\"node-wrapper\">\n        <div class=\"node-content-wrapper loading\">\n          <ng-container [ngTemplateOutlet]=\"loadingTemplate || defaultLoadingTemplate\"></ng-container>\n        </div>\n      </div>\n    </li>\n\n    <!-- If not loading and no children exist, show placeholder with empty message. -->\n    <li *ngIf=\"isEmpty(node)\" role=\"treeitem\">\n      <div class=\"node-wrapper\">\n        <div class=\"node-content-wrapper empty\">\n          <ng-container [ngTemplateOutlet]=\"emptyTemplate || defaultEmptyTemplate\"></ng-container>\n        </div>\n      </div>\n    </li>\n  </ul>\n</ng-template>\n\n<ng-template #togglePlaceholder>\n  <span class=\"toggle-children-placeholder\"></span>\n</ng-template>\n\n<ng-template #defaultTemplate let-node>\n  <a class=\"ahe-ui-link node-title\" href=\"javascript:void(0)\" (click)=\"nodeClick.emit(node)\">\n    {{ node?.title }}\n  </a>\n</ng-template>\n\n<ng-template #defaultLoadingTemplate>\n  <div class=\"ahe-ui-loading-spinner\">\n    <div class=\"loader\">\n      <span class=\"sr-only\">Content is loading</span>\n      <svg class=\"circular\" viewBox=\"25 25 50 50\" focusable=\"false\" aria-hidden=\"true\">\n        <circle\n          class=\"path\"\n          cx=\"50\"\n          cy=\"50\"\n          r=\"20\"\n          fill=\"none\"\n          stroke-width=\"2\"\n          stroke-miterlimit=\"10\"\n        ></circle>\n      </svg>\n    </div>\n  </div>\n</ng-template>\n\n<ng-template #defaultEmptyTemplate> Empty </ng-template>\n", styles: [".dpg-icon-dropdown-arrow-right:before,.ahe-icon-dropdown-arrow-right:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23265867' d='M10.343 6.343L16 12l-5.657 5.657-1.414-1.414L13.172 12 8.929 7.757z'/%3E%3C/svg%3E\")}.btn-secondary-light .dpg-icon-dropdown-arrow-right:before,.btn-secondary-light .ahe-icon-dropdown-arrow-right:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M10.343 6.343L16 12l-5.657 5.657-1.414-1.414L13.172 12 8.929 7.757z'/%3E%3C/svg%3E\")}.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .dpg-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared):hover:not(:disabled,.disabled) .ahe-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .dpg-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared):active:not(:disabled,.disabled) .ahe-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .dpg-icon-dropdown-arrow-right:before,.btn-icon:not(.ngx-shared).active:not(:disabled,.disabled) .ahe-icon-dropdown-arrow-right:before{content:url(\"data:image/svg+xml;charset=utf8, %3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23fff' d='M10.343 6.343L16 12l-5.657 5.657-1.414-1.414L13.172 12 8.929 7.757z'/%3E%3C/svg%3E\")}ul{list-style:none;padding:0}ul.tree-children{padding-left:33px;overflow:visible}ul li{margin:3px 0}.outline-none{outline:none}.toggle-children-wrapper{border:1px solid #d7d7d7;margin:0 3px 0 0;padding:0;min-width:30px;min-height:48px;background:#f7f7f7;display:flex;justify-content:center;align-items:center;overflow:hidden;cursor:pointer}.toggle-children-wrapper .toggle-children{background:none;margin:0 auto;transform:rotate(0);position:relative;transition:transform .25s ease}.toggle-children-wrapper .toggle-children:before{width:28px;height:28px;cursor:pointer;display:block;text-align:center}.toggle-children-wrapper.expanded .toggle-children{transform:rotate(90deg)}.outline-none,.outline-none div{outline:none}.node-wrapper{display:flex;align-items:stretch;font-family:proxima_novaregular}.node-wrapper .toggle-children-placeholder{background-color:#f7f7f7;display:block;top:0;padding:0;margin:0;min-width:33px;height:0}.node-wrapper .node-content-wrapper{position:relative;display:flex;align-items:center;background-color:#fff;border:1px solid #d7d7d7;flex-grow:1;flex-basis:auto;min-height:48px;padding:8px}.node-wrapper .node-content-wrapper.loading{display:block;text-align:center;line-height:32px}.node-wrapper .node-content-wrapper.empty{display:block;text-align:center;border:1px dashed #d7d7d7;line-height:32px}.node-wrapper .node-content-wrapper .ahe-ui-loading-spinner{height:32px;width:32px;display:inline-block}.node-wrapper .node-content-wrapper a.node-title{font-size:16px;font-weight:500;border:transparent;text-decoration:none;color:#007c91;border-bottom:1px solid transparent}.node-wrapper .node-content-wrapper a.node-title:hover{border-bottom:1px solid #007c91}.node-wrapper .node-content-wrapper a.node-title:focus{border:1px solid #007c91}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: KeyboardNavigationService }]; }, propDecorators: { nodesById: [{
                type: Input
            }], rootNodeId: [{
                type: Input
            }], expandedNodes: [{
                type: Input
            }], showToggleFn: [{
                type: Input
            }], isLoading: [{
                type: Input
            }], expanderClick: [{
                type: Output
            }], nodeClick: [{
                type: Output
            }], template: [{
                type: ContentChild,
                args: ['treeNodeTemplate', { static: false }]
            }], loadingTemplate: [{
                type: ContentChild,
                args: ['loadingTemplate', { static: false }]
            }], emptyTemplate: [{
                type: ContentChild,
                args: ['emptyTemplate', { static: false }]
            }], listItems: [{
                type: ViewChildren,
                args: ['listItem']
            }] } });

class NgxBonsaiModule {
}
NgxBonsaiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: NgxBonsaiModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
NgxBonsaiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: NgxBonsaiModule, declarations: [BonsaiTreeComponent], imports: [CommonModule], exports: [BonsaiTreeComponent] });
NgxBonsaiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: NgxBonsaiModule, providers: [KeyboardNavigationService], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: NgxBonsaiModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [BonsaiTreeComponent],
                    providers: [KeyboardNavigationService],
                    imports: [CommonModule],
                    exports: [BonsaiTreeComponent],
                }]
        }] });

/*
 * Public API Surface of bonsai-tree
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BonsaiTreeComponent, NgxBonsaiModule };
